import { useState } from 'react'
import { useLogger } from '@tumelo/logging'

// based off of https://usehooks.com/useLocalStorage/
export const useLocalStorage = <T>(key: string, defaultValue: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return defaultValue
    }
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch (error) {
      logger.logError(error, { extra: { where: 'useLocalStorage' } })
      return defaultValue
    }
  })
  const logger = useLogger()

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error) {
      logger.logError(error, { extra: { where: 'useLocalStorage' } })
    }
  }

  return [storedValue, setValue]
}

export const useSessionStorage = <T>(key: string, defaultValue: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const logger = useLogger()
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return defaultValue
    }
    try {
      const item = window.sessionStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch (error) {
      logger.logError(error, { extra: { where: 'useSessionStorage' } })
      return defaultValue
    }
  })

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error) {
      logger.logError(error, { extra: { where: 'useSessionStorage' } })
    }
  }

  return [storedValue, setValue]
}

export const useCookie = <T>(
  key: string,
  defaultValue: T,
  options?: { maxAge?: number; path?: string; sameSite?: 'strict' | 'lax' | 'none' }
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const logger = useLogger()
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return defaultValue
    }
    try {
      const name = `${key}=`
      const decodedCookie = decodeURIComponent(document.cookie)
      const cookieArray = decodedCookie.split(';')
      for (let i = 0; i < cookieArray.length; i++) {
        let cookie = cookieArray[i]
        while (cookie.charAt(0) === ' ') {
          cookie = cookie.substring(1)
        }
        if (cookie.indexOf(name) === 0) {
          const value = cookie.substring(name.length, cookie.length)
          return JSON.parse(value)
        }
      }
      return defaultValue
    } catch (error) {
      logger.logError(error, { extra: { where: 'useCookie' } })
      return defaultValue
    }
  })

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      if (typeof window !== 'undefined') {
        const cookieOptions = {
          maxAge: options?.maxAge ?? 3600, // 1 hour default
          path: options?.path ?? '/',
          sameSite: options?.sameSite ?? 'strict',
        }
        let cookieString = `${key}=${encodeURIComponent(JSON.stringify(valueToStore))}`
        cookieString += `; max-age=${cookieOptions.maxAge}`
        cookieString += `; path=${cookieOptions.path}`
        cookieString += `; samesite=${cookieOptions.sameSite}`
        document.cookie = cookieString
      }
    } catch (error) {
      logger.logError(error, { extra: { where: 'useCookie' } })
    }
  }

  return [storedValue, setValue]
}
